/**
 * HangarMax import wizard: source → preview → progress → result.
 *
 * Follows the FuelCsvImportDialog shape (parse, review, confirm, report) — the
 * review step is the point of the whole flow, since an import creates buildings,
 * aircraft and allocations in one go.
 */

import { useCallback, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowLeft, ArrowRight, Link2, Loader2, Upload } from 'lucide-react'

import { ImportPreview } from './ImportPreview'
import { ImportResultReport } from './ImportResultReport'
import {
  useExecuteHangarmaxImport,
  useFetchSharedLayout,
  useImportProgress,
  usePrepareHangarmaxImport,
  type PreparedImport,
} from '@/hooks/useHangarmaxImport'
import {
  HangarmaxParseError,
  parseHangarmaxText,
  type ExecuteResult,
  type ParsedBundle,
  type ParsedLayoutOption,
} from '@/lib/hangarmax-import'

type Step = 'source' | 'preview' | 'progress' | 'result'

interface HangarmaxImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locationId: string
  locationCenter: { lat: number; lng: number } | null
}

export function HangarmaxImportDialog({
  open,
  onOpenChange,
  locationId,
  locationCenter,
}: HangarmaxImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<Step>('source')
  const [bundle, setBundle] = useState<ParsedBundle | null>(null)
  const [selectedLayoutIndex, setSelectedLayoutIndex] = useState(0)
  const [shareUrl, setShareUrl] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [prepared, setPrepared] = useState<PreparedImport | null>(null)
  const [result, setResult] = useState<ExecuteResult | null>(null)

  const prepare = usePrepareHangarmaxImport()
  const execute = useExecuteHangarmaxImport()
  const fetchShare = useFetchSharedLayout()
  const { progress, setProgress, reset: resetProgress } = useImportProgress()

  const reset = useCallback(() => {
    setStep('source')
    setBundle(null)
    setSelectedLayoutIndex(0)
    setShareUrl('')
    setParseError(null)
    setPrepared(null)
    setResult(null)
    resetProgress()
  }, [resetProgress])

  const close = useCallback(() => {
    onOpenChange(false)
    // Let the close animation finish before wiping the content.
    setTimeout(reset, 200)
  }, [onOpenChange, reset])

  const handleFile = useCallback(async (file: File) => {
    setParseError(null)
    try {
      const parsed = parseHangarmaxText(await file.text())
      // Soft-deleted layouts are still in a location export; hide them unless
      // they are all there is.
      const live = parsed.layouts.filter((l) => !l.deleted)
      setBundle({ ...parsed, layouts: live.length > 0 ? live : parsed.layouts })
      setSelectedLayoutIndex(0)
    } catch (error) {
      setBundle(null)
      setParseError(
        error instanceof HangarmaxParseError
          ? [error.message, ...error.issues].join('\n')
          : 'Could not read that file.'
      )
    }
  }, [])

  /** Manifest entries + token from the share fetch, carried into the preview. */
  const shareStateRef = useRef<{
    token: string
    catalogEntries: PreparedImport['catalogEntries']
  } | null>(null)

  const handleShareFetch = useCallback(async () => {
    setParseError(null)
    const fetched = await fetchShare.mutateAsync(shareUrl)
    setBundle({
      kind: 'share_scene',
      layouts: [
        {
          layoutId: null,
          name: fetched.layoutName ?? 'Shared layout',
          deleted: false,
          sceneData: fetched.sceneData,
          itemCache: null,
          buildingCount: fetched.sceneData.buildings?.length ?? 0,
          itemCount: fetched.sceneData.items?.length ?? 0,
        },
      ],
      projectRef: null,
      exportVersion: null,
      rawEnvelope: null,
      sourceLocation: null,
    })
    setSelectedLayoutIndex(0)
    shareStateRef.current = {
      token: fetched.shareToken,
      catalogEntries: fetched.catalogEntries,
    }
  }, [fetchShare, shareUrl])

  const layout: ParsedLayoutOption | null = bundle?.layouts[selectedLayoutIndex] ?? null

  const handlePreview = useCallback(async () => {
    if (!bundle || !layout || !locationCenter) return
    const isShare = bundle.kind === 'share_scene'

    const output = await prepare.mutateAsync({
      locationId,
      locationCenter,
      bundle,
      layout,
      sourceKind: isShare ? 'share_link' : 'bundle_upload',
      shareToken: isShare ? shareStateRef.current?.token ?? null : null,
      catalogEntries: isShare ? shareStateRef.current?.catalogEntries : undefined,
    })

    setPrepared(output)
    setStep('preview')
  }, [bundle, layout, locationCenter, locationId, prepare])

  const handleExecute = useCallback(async () => {
    if (!prepared) return
    setStep('progress')
    try {
      const output = await execute.mutateAsync({
        locationId,
        prepared,
        onProgress: setProgress,
      })
      setResult(output)
      setStep('result')
    } catch {
      // The mutation surfaces a toast; drop back so the user can retry.
      setStep('preview')
    }
  }, [execute, locationId, prepared, setProgress])

  const progressPercent =
    progress && progress.total > 0
      ? Math.round((progress.completed / progress.total) * 100)
      : 0

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import layout from HangarMax</DialogTitle>
          <DialogDescription>
            Buildings, aircraft and procedural objects are brought into this
            location and can be re-synced as the HangarMax layout changes.
          </DialogDescription>
        </DialogHeader>

        {!locationCenter && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            This location has no coordinates yet. Set its latitude and longitude
            in General settings first — they are the scene origin for the import.
          </p>
        )}

        {step === 'source' && (
          <Tabs defaultValue="file">
            <TabsList>
              <TabsTrigger value="file">
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                Export file
              </TabsTrigger>
              <TabsTrigger value="share">
                <Link2 className="mr-1.5 h-3.5 w-3.5" />
                Share link
              </TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="space-y-3 pt-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) void handleFile(file)
                  event.target.value = ''
                }}
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload className="mr-2 h-4 w-4" />
                Choose HangarMax export
              </Button>
              <p className="text-xs text-muted-foreground">
                Accepts a layout export, a location export (all its layouts), or a
                raw scene file.
              </p>
            </TabsContent>

            <TabsContent value="share" className="space-y-3 pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="hm-share-url">HangarMax share link</Label>
                <div className="flex gap-2">
                  <Input
                    id="hm-share-url"
                    placeholder="https://hangarmax.com/shared/…"
                    value={shareUrl}
                    onChange={(event) => setShareUrl(event.target.value)}
                  />
                  <Button
                    variant="outline"
                    disabled={!shareUrl.trim() || fetchShare.isPending}
                    onClick={() => void handleShareFetch()}
                  >
                    {fetchShare.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Load
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Published layouts are served from a CDN and can be up to an hour
                behind the latest edit in HangarMax.
              </p>
            </TabsContent>

            {parseError && (
              <p className="mt-3 whitespace-pre-line rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {parseError}
              </p>
            )}

            {bundle && bundle.layouts.length > 1 && (
              <div className="mt-4 space-y-1.5">
                <Label>Layout</Label>
                <Select
                  value={String(selectedLayoutIndex)}
                  onValueChange={(value) => setSelectedLayoutIndex(Number(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {bundle.layouts.map((option, index) => (
                      <SelectItem key={`${option.layoutId ?? index}`} value={String(index)}>
                        {option.name} — {option.buildingCount} buildings,{' '}
                        {option.itemCount} items
                        {option.deleted ? ' (deleted)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {bundle && layout && (
              <p className="mt-3 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{layout.name}</span> —{' '}
                {layout.buildingCount} building{layout.buildingCount === 1 ? '' : 's'},{' '}
                {layout.itemCount} item{layout.itemCount === 1 ? '' : 's'}.
              </p>
            )}
          </Tabs>
        )}

        {step === 'preview' && prepared && <ImportPreview plan={prepared.plan} />}

        {step === 'progress' && (
          <div className="space-y-3 py-6">
            <Progress value={progressPercent} />
            <p className="text-sm text-muted-foreground">
              {progress
                ? `${progress.completed} of ${progress.total} — ${progress.label}`
                : 'Starting…'}
            </p>
          </div>
        )}

        {step === 'result' && result && <ImportResultReport result={result} />}

        <DialogFooter>
          {step === 'source' && (
            <>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                disabled={!bundle || !layout || !locationCenter || prepare.isPending}
                onClick={() => void handlePreview()}
              >
                {prepare.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Review import
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}

          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={() => setStep('source')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={() => void handleExecute()} disabled={execute.isPending}>
                {execute.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import
              </Button>
            </>
          )}

          {step === 'result' && <Button onClick={close}>Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
